import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useIntl } from 'react-intl';

import { isPostDeleteOrganizationSetupSearch } from '@src/containers/App/Onboarding/onboardingSetup';
import { buildCompleteRegistrationPath } from '@src/packages/users/utils/completeRegistrationPath';

import { errorNotification, infoNotification } from 'appRedux/actions';
import { ACCESS_TYPES, isPackedClaims, resolveOrgAccess } from 'packages/utils/access';
import logo from 'assets/images/logo.png';
import { auth } from '@src/firebase/firebase';
import SignUpService from '@src/services/SignUpService';

import styles from './styles.module.less';
import StepOne from './components/StepOne';
import StepTwo from './components/StepTwo';
import StepThree from './components/StepThree';
import StepIndicator from './components/StepIndicator';
import { SIGN_UP_UPDATE_STEP_1, SIGN_UP_UPDATE_STEP_2, SIGN_UP_UPDATE_STEP_3 } from './constants';

const ORGANIZATION_WIZARD_STEP = 1;

const Wizard = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const history = useHistory();
  const isOrganizationSetupMode = isPostDeleteOrganizationSetupSearch(search);
  const signUpService = new SignUpService();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSavingStep, setIsSavingStep] = useState(false);

  const {
    id: userId,
    firstName = '',
    lastName = '',
    onboardingStep = 0,
  } = useSelector(({ user }) => user.profile.data);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserAccess = useSelector(({ user }) => user.orgUser?.data?.access);
  const orgUserLoaded = useSelector(({ user }) => user.orgUser?.loaded);
  const orgId = useSelector(({ organizations }) => organizations.organization.id);
  const orgData = useSelector(({ organizations }) => organizations.organization.data || {});
  const orgDataSafe = {
    name: orgData?.name || '',
    phone: orgData?.phone || '',
    size: orgData?.size || '1-10',
    address: orgData?.address || { address: '' },
  };

  const organizationLoading = useSelector(
    ({ organizations }) => organizations.organization.save.loading,
  );
  const userLoading = useSelector(({ user }) => user.profile.save.loading);
  const orgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const isInvitedUser = Boolean(orgAccess) && orgAccess !== ACCESS_TYPES.OWNER;
  // A packed token has no org role until the org user document arrives. organizationChange
  // clears that document on select, so loaded === false means the read is still pending.
  const orgRolePending = isPackedClaims(claims) && orgUserLoaded === false;
  const isLoading = isSavingStep || organizationLoading || userLoading || orgRolePending;

  useEffect(() => {
    if (!userId || isInvitedUser || orgRolePending) return;

    if (isOrganizationSetupMode) {
      setCurrentStep(prev => (prev === 0 ? ORGANIZATION_WIZARD_STEP : prev));
      return;
    }

    if (onboardingStep >= 2) {
      setCurrentStep(2);
      return;
    }

    if (onboardingStep === 1) setCurrentStep(ORGANIZATION_WIZARD_STEP);
  }, [userId, onboardingStep, isInvitedUser, orgRolePending, isOrganizationSetupMode]);

  // Invitees complete the full profile on /complete-registration; only the backend activates them.
  useEffect(() => {
    if (isInvitedUser) {
      history.replace(buildCompleteRegistrationPath(orgId));
    }
  }, [history, isInvitedUser, orgId]);

  const saveUser = async newValues => {
    const uid = userId || localStorage.getItem('user_id');
    const userPayload = newValues || { firstName, lastName };

    try {
      setIsSavingStep(true);
      await signUpService.updateSignUp({
        uid,
        step: SIGN_UP_UPDATE_STEP_1,
        user: userPayload,
      });
      goNext();
    } catch (error) {
      dispatch(errorNotification(error?.message || 'Error saving onboarding step 1'));
    } finally {
      setIsSavingStep(false);
    }
  };

  const goNext = () => setCurrentStep(currentStep + 1);
  const goBack = () => setCurrentStep(currentStep - 1);

  const saveOrganization = async newValues => {
    const uid = userId || localStorage.getItem('user_id');
    const organizationPayload = newValues || orgDataSafe;

    try {
      setIsSavingStep(true);
      await signUpService.updateSignUp({
        uid,
        step: SIGN_UP_UPDATE_STEP_2,
        organization: organizationPayload,
      });
      goNext();
    } catch (error) {
      dispatch(errorNotification(error?.message || 'Error saving onboarding step 2'));
    } finally {
      setIsSavingStep(false);
    }
  };

  const handleFinish = async newValues => {
    dispatch(infoNotification(intl.formatMessage({ id: 'onboarding.stepThree.creatingDivision' })));

    const uid = userId || localStorage.getItem('user_id');
    const divisionPayload = newValues || {};

    try {
      setIsSavingStep(true);
      await signUpService.updateSignUp({
        uid,
        step: SIGN_UP_UPDATE_STEP_3,
        division: divisionPayload,
      });

      await new Promise(resolve => setTimeout(resolve, 3000));

      window.location.href = '/';
    } catch (error) {
      dispatch(errorNotification(error?.message || 'Error saving onboarding step 3'));
    } finally {
      setIsSavingStep(false);
    }
  };

  const steps = [
    <StepOne onNext={saveUser} initialData={{ firstName, lastName }} />,
    <StepTwo onNext={saveOrganization} onPrevious={goBack} initialData={orgDataSafe} />,
    <StepThree onFinish={handleFinish} onPrevious={goBack} loading={isSavingStep} />,
  ];

  return (
    <>
      <div className={`${styles.logoContainer} gx-mb-5`}>
        <img src={logo} alt="Guarnic" className={styles.logo} />
      </div>
      <div className={styles.stepContainer}>
        <StepIndicator step={currentStep} isInvitedUser={isInvitedUser} />
        {isLoading ? <Spin size="large" /> : steps[currentStep]}
      </div>
    </>
  );
};

export default Wizard;
